import { useState, useEffect } from 'react';
import './App.css';
import About from './About';
import Portfolio from './Portfolio';
import Contact from './Contact';
import Skills from './Skills';
import Certifications from './Certifications';
import Footer from './Footer';
import ThemeContext from './ThemeContext';

const NAV_SECTIONS = ['about', 'skills', 'certifications', 'portfolio', 'contact'];

function App() {
  const [darkMode, setDarkMode] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  const [activeSection, setActiveSection] = useState('about');

  // Sync dark mode with OS preference changes
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setDarkMode(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Apply dark class to body and html so overscroll background matches
  useEffect(() => {
    document.body.classList.toggle('dark', darkMode);
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  // Nav highlight + scroll fade-in observers with shared bottom-scroll fallback
  useEffect(() => {
    const sectionEls = NAV_SECTIONS.map((id) => document.getElementById(id)).filter(Boolean);
    const fadeEls = document.querySelectorAll('.fade-in');

    const navObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-56px 0px -70% 0px', threshold: 0 }
    );

    const fadeObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { rootMargin: '-40% 0px -40% 0px', threshold: 0 }
    );

    sectionEls.forEach((el) => navObserver.observe(el));
    fadeEls.forEach((el) => fadeObserver.observe(el));

    // Shared fallback: at page bottom, activate last nav item and reveal remaining fade-ins
    const handleScroll = () => {
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 8;
      if (atBottom) {
        setActiveSection(NAV_SECTIONS[NAV_SECTIONS.length - 1]);
        document.querySelectorAll('.fade-in:not(.visible)').forEach((el) => el.classList.add('visible'));
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      navObserver.disconnect();
      fadeObserver.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <ThemeContext.Provider value={{ darkMode, setDarkMode }}>
      <div className={`App${darkMode ? ' dark' : ''}`}>
        <header>
          <nav>
            <ul>
              <li className="nav-brand-item">
                <button className="nav-brand" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Fabian Trejo</button>
              </li>
              <li><a href="#about"    className={activeSection === 'about'          ? 'nav-active' : ''}>About</a></li>
              <li><a href="#skills"   className={activeSection === 'skills'         ? 'nav-active' : ''}>Skills</a></li>
              <li><a href="#certifications" className={activeSection === 'certifications' ? 'nav-active' : ''}>Certifications</a></li>
              <li><a href="#portfolio" className={activeSection === 'portfolio'     ? 'nav-active' : ''}>Portfolio</a></li>
              <li><a href="#contact"  className={activeSection === 'contact'        ? 'nav-active' : ''}>Contact</a></li>
              <li>
                <button
                  className="dark-toggle"
                  onClick={() => setDarkMode(!darkMode)}
                  aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {darkMode ? '☀ Light' : '☾ Dark'}
                </button>
              </li>
            </ul>
          </nav>
        </header>
        <main>
          <section id="about" className="fade-in">
            <About />
          </section>
          <section id="skills" className="fade-in">
            <Skills />
          </section>
          <section id="certifications" className="fade-in">
            <Certifications />
          </section>
          <section id="portfolio" className="fade-in">
            <Portfolio />
          </section>
          <section id="contact" className="fade-in">
            <Contact />
          </section>
        </main>
      </div>
      <Footer />
    </ThemeContext.Provider>
  );
}

export default App;
